
import { useMemo } from "react";
import { createDeterministicRNG } from "@/lib/utils/rng-utils";
import type { Word } from "@/lib/word-bank";

/**
 * Hook that creates a randomized version of words array for display
 * Uses a consistent seed to ensure the same shuffling every time
 * Guarantees words will never be shown in their correct sequential order
 */
export function useRandomizedWords(words: Word[]): Word[] {
  return useMemo(() => {
    // Create a deterministic shuffle based on the words themselves
    const wordsString = words.join(',');
    const rng = createDeterministicRNG(`display-${wordsString}`);
    
    // First shuffle
    let shuffledWords = rng.shuffle([...words]);
    
    // Check if by chance we got the original order (extremely unlikely but possible)
    const isOriginalOrder = shuffledWords.every((word, index) => word === words[index]);
    
    // If we somehow got the same order, shuffle again with a different seed
    if (isOriginalOrder) {
      const newRng = createDeterministicRNG(`alt-display-${wordsString}`);
      shuffledWords = newRng.shuffle([...words]);
    }
    
    // Additional check: ensure at least two words are out of position
    const positionDifferences = shuffledWords.filter((word, index) => word !== words[index]).length;
    if (positionDifferences < 2 && words.length > 2) {
      // Swap first and last elements to guarantee at least two differences
      [shuffledWords[0], shuffledWords[shuffledWords.length - 1]] = 
        [shuffledWords[shuffledWords.length - 1], shuffledWords[0]];
    }
    
    return shuffledWords;
  }, [words]);
}
